<template>
  <div class="n-popup">
    <div class="n-popup-reference" ref="reference">
      <slot name="reference">(reference slot missing)</slot>
    </div>

    <div class="n-popup-popup" ref="popup">
      <slot name="popup">(popup slot missing)</slot>
    </div>
  </div>
</template>

<script>
import tippy, { followCursor } from 'tippy.js'
import { NPopupConfig } from './NPopupConfig'

const hideOnEsc = {
    name: 'hideOnEsc',
    defaultValue: true,

    fn (instance) {
        function onKeyDown (event) {
            if (event.key === 'Escape' && instance.props.hideOnEsc) {
                instance.hide()
            }
        }

        return {
            onShow () {
                document.addEventListener('keydown', onKeyDown)
            },

            onHide () {
                document.removeEventListener('keydown', onKeyDown)
            }
        }
    }
}

export default {
    name: 'NPopup',

    props: {
        config: {
            type: NPopupConfig,
            default: () => new NPopupConfig()
        }
    },

    data () {
        return {
            tippy: null
        }
    },

    watch: {
        'config.direction': function () {
            this.setDirection()
        },

        'config.origin': function () {
            this.setOrigin()
        },

        'config.mode': function () {
            this.setMode()
        },

        'config.fixed': function () {
            this.setFixed()
        },

        'config.autoClose': function () {
            this.setAutoClose()
        },

        'config.offsetSkidding': function () {
            this.setOffset()
        },

        'config.offsetDistance': function () {
            this.setOffset()
        },

        'config.maxWidth': function () {
            this.setMaxWidth()
        },

        'config.duration': function () {
            this.setDuration()
        }
    },

    created () {
        this.config._component = this
    },

    mounted () {
        const options = {
            arrow: false,
            content: this.$refs.popup,
            delay: 0,
            duration: 0,
            ignoreAttributes: true,
            interactive: true,
            plugins: [
                followCursor,
                hideOnEsc
            ],

            onHidden: () => {
                this.$emit('close', this.config)
                this.config.trigger('close')
            },

            onShow: () => {
                this.$emit('open', this.config)
                this.config.trigger('open')
            }
        }

        this.tippy = tippy(
            this.$refs.reference,
            options
        )

        this.setDirection()
        this.setOrigin()
        this.setMode()
        this.setFixed()
        this.setAutoClose()
        this.setOffset()
        this.setMaxWidth()
        this.setDuration()
    },

    beforeDestroy () {
        if (this.tippy) {
            this.tippy.destroy()
            this.tippy = null
        }
    },

    methods: {
        open () {
            if (this.tippy) {
                this.tippy.show()
            }
        },

        close () {
            if (this.tippy) {
                this.tippy.hide()
            }
        },

        setDirection () {
            if (!this.tippy) {
                return
            }

            let placement

            switch (this.config.direction) {
            case 'n':
                placement = 'top'
                break

            case 's':
                placement = 'bottom'
                break

            case 'e':
                placement = 'right'
                break

            case 'w':
                placement = 'left'
                break

            case 'ne':
                placement = 'top-start'
                break

            case 'nw':
                placement = 'top-end'
                break

            case 'sw':
                placement = 'bottom-end'
                break

            default:
                placement = 'bottom-start'
            }

            this.tippy.setProps({
                placement: placement
            })
        },

        setOrigin () {
            if (!this.tippy) {
                return
            }

            this.tippy.setProps({
                followCursor:
                    this.config.origin === 'mouse'
                        ? 'initial'
                        : false
            })
        },

        setMode () {
            if (!this.tippy) {
                return
            }

            this.tippy.setProps({
                trigger:
                    this.config.mode === 'click'
                        ? 'click'
                        : this.config.mode === 'hover'
                            ? 'mouseenter focus'
                            : 'manual'
            })
        },

        setFixed () {
            if (!this.tippy) {
                return
            }

            this.tippy.setProps({
                popperOptions: {
                    strategy:
                        this.config.fixed
                            ? 'fixed'
                            : 'absolute'
                }
            })
        },

        setAutoClose () {
            if (!this.tippy) {
                return
            }

            this.tippy.setProps({
                hideOnEsc: this.config.autoClose,
                hideOnClick: this.config.autoClose
            })
        },

        setOffset () {
            if (!this.tippy) {
                return
            }

            this.tippy.setProps({
                offset: [
                    this.config.offsetSkidding,
                    this.config.offsetDistance
                ]
            })
        },

        setMaxWidth () {
            if (!this.tippy) {
                return
            }

            this.tippy.setProps({
                maxWidth:
                    this.config.maxWidth === null
                        ? 'none'
                        : this.config.maxWidth
            })
        },

        setDuration () {
            if (!this.tippy) {
                return
            }

            this.tippy.setProps({
                duration: this.config.duration
            })
        }
    }
}
</script>

<style lang="scss">
.n-popup {
    display: inline-block;

    .tippy-box {
        transition-property: visibility, opacity;

        &[data-animation='fade'][data-state='hidden'] {
            opacity: 0;
        }
    }
}
</style>


// WEBPACK FOOTER //
// src/components/shared/npopup/NPopup.vue